
import { differenceInDays, addDays } from 'date-fns';
import { CycleData, CycleDay, CyclePhase } from './types';
import { hormoneBaselines, phaseDurations } from './constants';

// Generate a typical cycle based on last period start
export function generateCycleData(lastPeriodStart: Date | null, periodLength = 5, cycleLength = 28): CycleData | null {
  if (!lastPeriodStart) return null;
  
  const today = new Date();
  const periodStartDate = new Date(lastPeriodStart);
  const predictedEndDate = addDays(periodStartDate, periodLength - 1);
  
  // Calculate next period and ovulation dates
  const predictedNextPeriod = addDays(periodStartDate, cycleLength);
  const predictedOvulation = addDays(periodStartDate, Math.floor(cycleLength / 2) - 2);
  
  // Calculate current day in cycle
  const daysSinceLastPeriod = differenceInDays(today, periodStartDate);
  const currentDay = daysSinceLastPeriod % cycleLength + 1;
  const daysUntilNextPeriod = cycleLength - (daysSinceLastPeriod % cycleLength);
  
  // Determine current phase
  let currentPhase: CyclePhase;
  if (currentDay <= phaseDurations.menstruation) {
    currentPhase = 'menstruation';
  } else if (currentDay <= phaseDurations.menstruation + phaseDurations.follicular) {
    currentPhase = 'follicular';
  } else if (currentDay <= phaseDurations.menstruation + phaseDurations.follicular + phaseDurations.ovulation) {
    currentPhase = 'ovulation';
  } else if (currentDay <= cycleLength - phaseDurations.premenstrual) {
    currentPhase = 'luteal';
  } else {
    currentPhase = 'premenstrual';
  }
  
  // Generate day-by-day hormone levels
  const days: CycleDay[] = [];
  let dayCounter = 1;
  let phaseDay = 1;
  let currentPhaseTracking: CyclePhase = 'menstruation';
  
  for (let i = 0; i < cycleLength; i++) {
    const absoluteDate = addDays(periodStartDate, i);
    const isPrediction = absoluteDate > today;
    
    // Determine phase for this day
    if (dayCounter > phaseDurations[currentPhaseTracking as keyof typeof phaseDurations]) {
      dayCounter = 1;
      phaseDay = 1;
      if (currentPhaseTracking === 'menstruation') currentPhaseTracking = 'follicular';
      else if (currentPhaseTracking === 'follicular') currentPhaseTracking = 'ovulation';
      else if (currentPhaseTracking === 'ovulation') currentPhaseTracking = 'luteal';
      else if (currentPhaseTracking === 'luteal') currentPhaseTracking = 'premenstrual';
    }
    
    // Get hormone levels for this phase and day
    const phaseHormones = hormoneBaselines[currentPhaseTracking];
    const hormones = {
      estrogen: phaseHormones.estrogen[Math.min(phaseDay - 1, phaseHormones.estrogen.length - 1)],
      progesterone: phaseHormones.progesterone[Math.min(phaseDay - 1, phaseHormones.progesterone.length - 1)],
      testosterone: phaseHormones.testosterone[Math.min(phaseDay - 1, phaseHormones.testosterone.length - 1)],
      fsh: phaseHormones.fsh[Math.min(phaseDay - 1, phaseHormones.fsh.length - 1)],
      lh: phaseHormones.lh[Math.min(phaseDay - 1, phaseHormones.lh.length - 1)]
    };
    
    days.push({
      dayNumber: i + 1,
      date: absoluteDate,
      phase: currentPhaseTracking,
      isToday: false, // This will be set by the component when needed
      isCurrent: currentDay === i + 1,
      isPeriod: currentPhaseTracking === 'menstruation',
      isFertile: currentPhaseTracking === 'follicular' || currentPhaseTracking === 'ovulation',
      isOvulation: currentPhaseTracking === 'ovulation',
      // Additional properties
      day: i + 1,
      phaseDay: phaseDay,
      absoluteDate: absoluteDate,
      hormones,
      prediction: isPrediction
    });
    
    dayCounter++;
    phaseDay++;
  }
  
  return {
    cycle: {
      startDate: periodStartDate,
      endDate: addDays(periodStartDate, cycleLength - 1),
      averageLength: cycleLength
    },
    dailyData: [], // This field needs to be populated elsewhere
    currentPhase,
    currentDay,
    cycleLength,
    periodStartDate,
    predictedNextPeriod,
    predictedOvulation,
    daysUntilNextPeriod,
    days,
    // Additional fields
    predictedEndDate,
    periodLength,
    daysSinceLastPeriod
  };
}
